import { CustomDatePipe, MatButtonLoaderStandaloneDirective, requiredTextValidator } from '@abovo/shared-angular-utils';
import { ACL_TARGET_APPROVALS, AclRestrictDirective, getCurrentUser } from '@abovo/shared-auth';
import { ApprovalRequest, EntityPermissionRestriction, Restriction } from '@abovo/shared-models';
import { TextInputComponent, useConfirmDialog } from '@abovo/shared-ui-components';
import { isDefined } from '@abovo/shared-utils';
import { AttachmentService } from '@abovo/webvis-data-access';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, output, signal, viewChild } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatTooltip } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import { SHARE_LINK_TARGET_ATTACHMENT, ShareLinkIndicatorComponent } from '../../../../features/share-link';
import { ReviewerStatusSlugs } from '../../constants/approval-request.constants';
import { ApprovalFeedbackCommentsService } from '../../services/approval-feedback-comments.service';
import { ApprovalFeedbackReviewersDisplayComponent } from '../../views/approval-feedback-reviewers-display.component';
import { ApprovalUrlPreviewComponent } from '../../views/approval-url-preview.component';
import {
   ApprovalFeedbackCommentsComponent,
   CommentEditPayload,
   CommentSubmitPayload,
} from '../approval-feedback-comments/approval-feedback-comments.component';

export interface ApprovalFeedbackDialogData {
   approvalRequest: ApprovalRequest;
   config: { readonly?: boolean; approve?: boolean };
}

@UntilDestroy()
@Component({
   selector: 'webvis-feature-approval-feedback-dialog',
   templateUrl: './approval-feedback-dialog.component.html',
   changeDetection: ChangeDetectionStrategy.OnPush,
   host: { class: 'flex flex-col flex-1 min-h-0 overflow-hidden' },
   styles: [
      `
         :host::ng-deep .mat-button-toggle-label-content {
            line-height: 2rem;
         }
      `,
   ],
   imports: [
      TranslatePipe,
      MatButton,
      MatIconButton,
      MatButtonToggleGroup,
      MatButtonToggle,
      MatDivider,
      ReactiveFormsModule,
      FaIconComponent,
      TextInputComponent,
      MatCheckbox,
      MatTooltip,
      CustomDatePipe,
      ApprovalUrlPreviewComponent,
      ApprovalFeedbackReviewersDisplayComponent,
      ApprovalFeedbackCommentsComponent,
      MatButtonLoaderStandaloneDirective,
      CommonModule,
      AclRestrictDirective,
      ShareLinkIndicatorComponent,
   ],
   providers: [ApprovalFeedbackCommentsService],
})
export class ApprovalFeedbackDialogComponent implements OnInit {
   private readonly fb = inject(FormBuilder);
   readonly data = inject<ApprovalFeedbackDialogData>(MAT_DIALOG_DATA);
   private readonly dialogRef = inject<MatDialogRef<ApprovalFeedbackDialogComponent>>(MatDialogRef);
   private readonly confirmDialog = useConfirmDialog();
   private readonly attachmentService = inject(AttachmentService);
   private readonly commentsService = inject(ApprovalFeedbackCommentsService);
   private readonly store = inject(Store);
   readonly currentUser = this.store.selectSignal(getCurrentUser);

   private previewComponent = viewChild(ApprovalUrlPreviewComponent);
   private commentsComponent = viewChild(ApprovalFeedbackCommentsComponent);

   readonly save = output<{ approved: boolean; comment?: string; override?: boolean }>();
   readonly saving = signal(false);

   readonly approvedControl = this.fb.control<boolean | null>(this.data.config.approve ?? null, Validators.required);
   readonly elaborationControl = this.fb.control<string>('');
   readonly overrideControl = this.fb.control<boolean>(false);
   readonly approvalChoiceMade = signal(isDefined(this.data.config.approve));

   readonly comments = this.commentsService.comments;
   readonly commentsLoading = this.commentsService.loading;
   readonly currentSlide = signal(1);
   readonly useFullScreen = signal(false);

   readonly userIsReviewer = computed(() => !!this.data.approvalRequest.reviewers?.find((reviewer) => reviewer.user?.id === this.currentUser()?.id));

   readonly commentRestriction: Restriction = { target: ACL_TARGET_APPROVALS, operation: 'read', description: 'ApprovalFeedbackDialogComponent view comments' };

   /** Attachment id of the reviewed deck; null when the approval only carries a drive link. */
   readonly attachmentId = computed(() => this.data.approvalRequest.approval_request_attachments?.[0]?.attachment_id ?? null);
   readonly shareLinkTargetConfig = SHARE_LINK_TARGET_ATTACHMENT;

   readonly attachmentUrl = computed(() => {
      const attachmentId = this.attachmentId();
      return attachmentId === null ? null : this.attachmentService.getViewUrl(attachmentId);
   });
   readonly overridePermission: EntityPermissionRestriction<ApprovalRequest> = {
      entity: this.data.approvalRequest,
      permission: 'can_override',
      description: 'ApprovalFeedbackDialogComponent override checkbox',
   };

   ngOnInit(): void {
      void this.commentsService.load(this.data.approvalRequest.id);

      if (!this.data.config.readonly) {
         this.approvedControl.valueChanges.pipe(untilDestroyed(this)).subscribe((approved) => {
            this.approvalChoiceMade.set(true);
            this.syncElaborationValidator(approved);
         });

         // A control's initial value never emits valueChanges, so apply the validator for the intent we were opened with (GER-5508)
         this.syncElaborationValidator(this.approvedControl.value);

         this.initApprovalState();
      }
   }

   async onClose(): Promise<void> {
      if (this.approvedControl.dirty) {
         const { afterClosed } = this.confirmDialog.open({
            title: 'approval.unsaved_changes_title',
            message: 'approval.unsaved_changes_message',
         });
         const confirmed = await afterClosed;
         if (confirmed) {
            this.dialogRef.close();
         }
      } else {
         this.dialogRef.close();
      }
   }

   onFinish(): void {
      const approved = this.approvedControl.value;
      if (this.approvedControl.invalid || approved === null) return;
      if (this.approvedControl.value === false && this.elaborationControl.invalid) {
         this.elaborationControl.markAsTouched();
         return;
      }

      this.saving.set(true);

      this.save.emit({
         approved,
         comment: this.elaborationControl.value?.trim() || undefined,
         override: this.overrideControl.value || undefined,
      });
   }

   async onCommentSubmit(payload: CommentSubmitPayload): Promise<void> {
      try {
         await this.commentsService.add(this.data.approvalRequest.id, payload);
      } catch {
         this.commentsComponent()?.restorePayload(payload);
      } finally {
         this.commentsComponent()?.clearReplyState();
      }
   }

   async onCommentEdit({ commentId, content }: CommentEditPayload): Promise<void> {
      try {
         await this.commentsService.edit(this.data.approvalRequest.id, { commentId, content });
         this.commentsComponent()?.clearEditState();
      } catch {
         this.commentsComponent()?.restoreEdit(commentId, content);
      }
   }

   async onCommentDelete(commentId: number): Promise<void> {
      const { afterClosed } = this.confirmDialog.open({
         title: 'approval.delete_comment_title',
         message: 'approval.delete_comment_message',
         removeButton: 'general.delete',
      });
      const confirmed = await afterClosed;
      if (!confirmed) {
         return;
      }

      this.commentsComponent()?.loadingDeleteId.set(commentId);

      try {
         await this.commentsService.delete(this.data.approvalRequest.id, commentId);
      } catch {
         // snackbar already shown by the service
      } finally {
         this.commentsComponent()?.clearDeleteState();
      }
   }

   onGoToSlide(slideNumber: number): void {
      this.previewComponent()?.goToPage(slideNumber);
   }

   onPageChange(page: number): void {
      this.currentSlide.set(page);
   }

   /** A rejection always needs an elaboration, an approval does not. */
   private syncElaborationValidator(approved: boolean | null): void {
      if (approved === false) {
         this.elaborationControl.setValidators(requiredTextValidator);
      } else {
         this.elaborationControl.clearValidators();
      }
      this.elaborationControl.updateValueAndValidity();
   }

   private initApprovalState(): void {
      const currentReviewer = this.data.approvalRequest.reviewers?.find((reviewer) => reviewer.user?.id === this.currentUser()?.id);
      const currentReviewerStatusSlug = currentReviewer?.status.slug;
      if (currentReviewerStatusSlug && currentReviewerStatusSlug !== ReviewerStatusSlugs.pending) {
         const approved = currentReviewerStatusSlug === ReviewerStatusSlugs.approved;
         this.approvedControl.setValue(approved, { emitEvent: true });
         this.approvedControl.markAsDirty();
      }
   }
}
